import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { fmtMoney, fmtNumber } from '../../utils/format';

/** A4 documents with prices (quote, pro-forma invoice): browser print / save as PDF. */
export const PRINT_CSS = `
@page { size: A4; margin: 16mm; }
@media print {
  .no-print { display: none !important; }
  html, body { background: #fff !important; }
  .sheet { box-shadow: none !important; margin: 0 !important; padding: 0 !important; width: auto !important; min-height: 0 !important; }
  [contenteditable] { outline: none !important; background: none !important; }
}
.sheet [contenteditable]:hover { background: rgba(66,120,152,0.08); }
.sheet [contenteditable]:focus { outline: 2px solid rgba(66,120,152,0.4); background: rgba(66,120,152,0.05); }
`;

/** Text the user can correct right on the sheet before printing (same as PrintFormPage). */
export const Edit = ({ children, block, sx }) => (
    <Box component={block ? 'div' : 'span'} contentEditable suppressContentEditableWarning
         sx={{ borderRadius: '3px', px: 0.25, whiteSpace: 'pre-wrap', ...sx }}>
        {children}
    </Box>
);

/** Grey page with the white A4 sheet; `toolbar` sits above it and is not printed. */
export function Sheet({ toolbar, children }) {
    return (
        <Box sx={{ bgcolor: '#E5E7EB', minHeight: '100vh', py: 3, px: { xs: 1, sm: 3 } }}>
            <style>{PRINT_CSS}</style>
            {toolbar}
            <Box className="sheet" sx={{
                bgcolor: '#fff', width: '210mm', maxWidth: '100%', minHeight: '297mm', mx: 'auto', p: { xs: 3, sm: '16mm' },
                boxShadow: '0 4px 24px rgba(0,0,0,0.12)', fontFamily: 'Montserrat, Arial, sans-serif', color: '#111827', boxSizing: 'border-box',
            }}>
                {children}
            </Box>
        </Box>
    );
}

/** Letterhead: the firm's logo (Company page) next to its details. */
export function Letterhead({ company, logo }) {
    const { t } = useTranslation();
    const companyLine = [company.address, company.city].filter(Boolean).join(', ');
    const contactLine = [company.phone, company.email, company.website].filter(Boolean).join(' · ');
    return (
        <Box sx={{ borderBottom: '2px solid #427898', pb: 1.5, mb: 4, display: 'flex', alignItems: 'center', gap: 2.5 }}>
            {logo && <Box component="img" src={logo} alt="" sx={{ maxWidth: 140, maxHeight: 72, objectFit: 'contain', flexShrink: 0 }} />}
            <Box sx={{ minWidth: 0 }}>
                <Edit block sx={{ fontSize: 20, fontWeight: 800, color: '#427898' }}>{company.name}</Edit>
                {companyLine && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{companyLine}</Edit>}
                {contactLine && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{contactLine}</Edit>}
                {company.taxNumber && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{`${t('print.taxNo')}: ${company.taxNumber}`}</Edit>}
            </Box>
        </Box>
    );
}

/**
 * Positions table with the totals: without PDV one total, with PDV the base, the PDV and the total to pay.
 * `doc` has items, net, vatEnabled, vatRate, vatAmount, total. `showCategory` adds each position's category.
 */
export function ItemsTable({ doc, showCategory }) {
    const { t } = useTranslation();
    const rate = fmtNumber(doc.vatRate);
    const totals = doc.vatEnabled
        ? [[t('vat.base'), doc.net], [t('vat.vat', { rate }), doc.vatAmount], [t('vat.totalToPay'), doc.total, true]]
        : [[t('vat.totalNoVat'), doc.net, true]];
    return (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 4, fontSize: 13, '& td, & th': { px: 1, py: '8px', borderBottom: '1px solid #E5E7EB', verticalAlign: 'top' } }}>
            <thead>
                <Box component="tr" sx={{ bgcolor: '#F3F4F6', '& th': { fontSize: 11.5, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left' } }}>
                    <th style={{ width: 28 }}>#</th>
                    <th>{t('quotes.items.description')}</th>
                    <th style={{ textAlign: 'right', width: 90 }}>{t('quotes.items.quantity')}</th>
                    <th style={{ textAlign: 'right', width: 100 }}>{t('quotes.items.unitPrice')}</th>
                    <th style={{ textAlign: 'right', width: 110 }}>{t('quotes.items.total')}</th>
                </Box>
            </thead>
            <tbody>
                {(doc.items || []).map((i, idx) => (
                    <tr key={i.id}>
                        <td style={{ color: '#6B7280' }}>{idx + 1}</td>
                        <td>
                            <Edit>{i.description}</Edit>
                            {showCategory && i.category && <Box sx={{ fontSize: 11.5, color: '#6B7280', mt: 0.25 }}>{i.category.name}</Box>}
                        </td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><Edit>{`${fmtNumber(i.quantity)}${i.unit ? ` ${i.unit}` : ''}`}</Edit></td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><Edit>{fmtMoney(i.unitPrice)}</Edit></td>
                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600 }}><Edit>{fmtMoney(i.total)}</Edit></td>
                    </tr>
                ))}
                {totals.map(([label, value, strong], idx) => (
                    <Box component="tr" key={label} sx={{ '& td': { borderBottom: 0, pt: idx === 0 ? 1.5 : 0.5, ...(idx === 0 && { borderTop: '2px solid #111827' }) } }}>
                        <td colSpan={4} style={{ textAlign: 'right', fontSize: strong ? 14 : 13, fontWeight: strong ? 700 : 500 }}>{label}</td>
                        <td style={{ textAlign: 'right', fontSize: strong ? 16 : 13, fontWeight: strong ? 800 : 600, whiteSpace: 'nowrap' }}><Edit>{fmtMoney(value)}</Edit></td>
                    </Box>
                ))}
            </tbody>
        </Box>
    );
}
